import { useState } from "react";
import PropTypes from "prop-types";
import {
  Banner,
  Chip,
  ClientName,
  CopyList,
  CopyRow,
  Fact,
  FactRow,
  FactsStrip,
  FactsTop,
  FileChip,
  FileSwitcher,
  LinkButton,
  SenderNote,
} from "../BoardAppealsStyled";
import { daysLeftLabel, deadlineTone, decidedTone, formatDay } from "../utils";

export const attachmentId = (att) => `attachment:${att.emailId}:${att.index}`;

const AppealFacts = ({
  card,
  viewedId,
  onViewFile,
  decisionId,
  files,
  onOpenAttachment,
  onPick,
  picking,
  pickError,
}) => {
  const [choosing, setChoosing] = useState(false);
  const attachments = files?.attachments || [];
  const others = attachments.filter((a) => !a.isDecision);
  const choices = [
    ...attachments
      .filter((a) => a.isDecision)
      .map((a) => ({
        key: attachmentId(a),
        label: a.name,
        where: "attached to an email",
        open: () => onOpenAttachment(a),
        choice: { emailId: a.emailId, index: a.index },
      })),
    ...(files?.copies || []).map((c) => ({
      key: c.id,
      label: c.date ? `${formatDay(c.date)} · ${c.name}` : c.name,
      where: "in the client's file",
      open: () => onViewFile(c.id),
      choice: { documentId: c.id },
    })),
  ];
  const viewingDecision = Boolean(decisionId) && viewedId === decisionId;
  const dateDisagrees =
    viewingDecision &&
    card.documentDate &&
    card.boardDate &&
    card.documentDate !== card.boardDate;

  const pick = async (choice) => {
    if (await onPick(choice)) setChoosing(false);
  };

  return (
    <FactsStrip>
      <FactsTop>
        <ClientName>{card.clientName || "Unnamed client"}</ClientName>
        {card.canDecide ? (
          <Chip $tone={deadlineTone(card)}>{daysLeftLabel(card.daysLeft)}</Chip>
        ) : (
          <Chip $tone={decidedTone(card.status)}>{card.statusLabel}</Chip>
        )}
      </FactsTop>
      <FactRow>
        <Fact>
          Board decision{" "}
          <strong>{card.boardDate ? formatDay(card.boardDate) : "date not on file"}</strong>
        </Fact>
        {card.deadline ? (
          <Fact>
            Notice of Appeal due <strong>{formatDay(card.deadline)}</strong>
            {card.deadlineOnCalendar ? " · on the calendar" : " · not on the calendar"}
          </Fact>
        ) : null}
        {card.intakeNumber ? (
          <Fact>
            Litify <strong>{card.intakeNumber}</strong>
            {card.intakeStatus ? ` · ${card.intakeStatus}` : ""}
            {card.intakeOwner ? ` · ${card.intakeOwner}` : ""}
          </Fact>
        ) : (
          <Fact>No Court intake in Litify yet</Fact>
        )}
        {typeof card.waitingDays === "number" ? (
          <Fact>
            Waiting <strong>{card.waitingDays}</strong> day
            {card.waitingDays === 1 ? "" : "s"}
          </Fact>
        ) : null}
      </FactRow>
      {card.requestNote ? (
        <SenderNote>
          <strong>{card.sentBy?.name || "Staff"}:</strong> {card.requestNote}
        </SenderNote>
      ) : null}
      {dateDisagrees ? (
        <Banner $tone="review" style={{ margin: 0 }}>
          This file is dated {formatDay(card.documentDate)}, but Litify has the Board
          decision on {formatDay(card.boardDate)}. Check it is the right one.
        </Banner>
      ) : null}
      {decisionId || others.length || choices.length ? (
        <FileSwitcher aria-label="Files for this appeal">
          {decisionId ? (
            <FileChip
              type="button"
              $active={viewingDecision}
              title={card.documentName}
              onClick={() => onViewFile(decisionId)}
            >
              Board decision
              {card.documentDate ? ` · ${formatDay(card.documentDate)}` : ""}
              {card.decisionInCourtFile ? " · in the Court file" : ""}
            </FileChip>
          ) : null}
          {others.map((a) => (
            <FileChip
              key={attachmentId(a)}
              type="button"
              $active={viewedId === attachmentId(a)}
              title={a.name}
              onClick={() => onOpenAttachment(a)}
            >
              {a.name}
            </FileChip>
          ))}
          {choices.length ? (
            <LinkButton type="button" onClick={() => setChoosing((v) => !v)}>
              {choosing ? "Keep this one" : "Use a different copy"}
            </LinkButton>
          ) : null}
        </FileSwitcher>
      ) : null}
      {choosing ? (
        <CopyList aria-label="Other copies of the Board decision">
          {choices.map((c) => (
            <CopyRow key={c.key} $active={viewedId === c.key}>
              <LinkButton type="button" onClick={c.open} title={c.label}>
                {c.label}
              </LinkButton>
              <span>{c.where}</span>
              <FileChip type="button" disabled={picking} onClick={() => pick(c.choice)}>
                Use this one
              </FileChip>
            </CopyRow>
          ))}
        </CopyList>
      ) : null}
      {pickError ? (
        <Banner role="alert" style={{ margin: 0 }}>
          {pickError}
        </Banner>
      ) : null}
    </FactsStrip>
  );
};

AppealFacts.propTypes = {
  card: PropTypes.object.isRequired,
  viewedId: PropTypes.string,
  onViewFile: PropTypes.func.isRequired,
  decisionId: PropTypes.string,
  files: PropTypes.shape({
    attachments: PropTypes.array,
    copies: PropTypes.array,
  }),
  onOpenAttachment: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  picking: PropTypes.bool,
  pickError: PropTypes.string,
};

export default AppealFacts;
